import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminUser } from '../actions/userActions';
import { onSessionEnded } from '../domain/Auth';

/**
 * Asks for the admin's record on behalf of the session in place now. If that
 * session ends before `auth/me` answers, the answer is dropped: kept, it would
 * restore the admin the teardown just cleared, and whoever signs in next would
 * inherit them, since a loaded admin is never fetched again.
 *
 * The listener lives as long as the request, not the effect, so a re-run
 * effect cannot leave an earlier request unguarded.
 */
const requestAdminUser = (dispatch) => {
  let sessionEnded = false;
  const stopListening = onSessionEnded(() => {
    sessionEnded = true;
  });
  Promise.resolve(dispatch(getAdminUser({ isCurrent: () => !sessionEnded }))).finally(
    stopListening,
  );
};

/**
 * Fetches the signed-in admin's own record once there is a session and it is
 * not loaded yet — after boot restores a session, after sign-in, and after
 * another tab signs in. The shell is held back until the first attempt
 * settles, because the account menu and the role-gated rail read it; if that
 * attempt fails the shell shows anyway, as it always has.
 *
 * @param {boolean} isSignedIn
 * @returns {{ isLoading: boolean }}
 */
export const useAdminUserBootstrap = (isSignedIn) => {
  const dispatch = useDispatch();
  const adminUserId = useSelector((state) => state.users.adminUser?.id);
  // undefined: not asked yet; true: in flight; false: answered (either way).
  const request = useSelector((state) => state.loading.GET_ADMIN_USER);

  useEffect(() => {
    if (isSignedIn && !adminUserId) requestAdminUser(dispatch);
  }, [isSignedIn, adminUserId, dispatch]);

  return { isLoading: isSignedIn && !adminUserId && request !== false };
};

export default useAdminUserBootstrap;
